import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatPrice, formatKm, getEcoLabelColor } from '@/lib/utils';
import { Calendar, Gauge, Fuel, Zap } from 'lucide-react';

export function VehicleCard({ vehicle }) {
    const displayTitle = vehicle.custom_title || vehicle.title || `${vehicle.brand} ${vehicle.model}`;
    const displayPrice = vehicle.custom_price || vehicle.price;
    const mainImage = vehicle.images?.[0];

    return (
        <Link to={`/coches/${vehicle.id}`} data-testid="vehicle-card">
            <Card className="group overflow-hidden rounded-xl border shadow-sm hover:shadow-md transition-shadow duration-200 bg-white cursor-pointer">
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden bg-[var(--brand-gray-soft)]">
                    {mainImage ? (
                        <img
                            src={mainImage}
                            alt={displayTitle}
                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-[hsl(var(--muted-foreground))]">
                            <Car className="h-12 w-12 opacity-30" />
                        </div>
                    )}

                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex gap-1.5">
                        {vehicle.status === 'reserved' && (
                            <Badge className="bg-amber-500/90 text-white border-0 text-xs font-medium" data-testid="vehicle-status-badge">
                                Reservado
                            </Badge>
                        )}
                        {vehicle.status === 'sold' && (
                            <Badge className="bg-red-500/90 text-white border-0 text-xs font-medium" data-testid="vehicle-status-badge">
                                Vendido
                            </Badge>
                        )}
                        {vehicle.featured && (
                            <Badge className="bg-[var(--brand-blue)]/90 text-white border-0 text-xs font-medium">
                                Destacado
                            </Badge>
                        )}
                    </div>

                    {vehicle.eco_label && (
                        <div className="absolute top-2 right-2">
                            <Badge variant="outline" className={`text-xs font-medium border ${getEcoLabelColor(vehicle.eco_label)}`} data-testid="vehicle-eco-label">
                                {vehicle.eco_label}
                            </Badge>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="p-4">
                    <h3 className="font-[var(--font-display)] font-semibold text-sm leading-tight text-[hsl(var(--foreground))] line-clamp-2 mb-2" data-testid="vehicle-card-title">
                        {displayTitle}
                    </h3>

                    <p className="font-[var(--font-display)] text-xl font-bold text-[var(--brand-navy)] mb-3" data-testid="vehicle-card-price">
                        {formatPrice(displayPrice)}
                    </p>

                    <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                        {vehicle.year && (
                            <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {vehicle.year}
                            </span>
                        )}
                        {vehicle.km != null && (
                            <span className="flex items-center gap-1">
                                <Gauge className="h-3 w-3" />
                                {formatKm(vehicle.km)}
                            </span>
                        )}
                        {vehicle.fuel && (
                            <span className="flex items-center gap-1">
                                <Fuel className="h-3 w-3" />
                                {vehicle.fuel}
                            </span>
                        )}
                        {vehicle.horsepower && (
                            <span className="flex items-center gap-1">
                                <Zap className="h-3 w-3" />
                                {vehicle.horsepower} cv
                            </span>
                        )}
                    </div>
                </div>
            </Card>
        </Link>
    );
}

function Car(props) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
            <circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/>
        </svg>
    );
}
